import { Pair } from '@/components/market/market';
import { usePrice } from '@/store/price';
import { useTradeStore } from '@/store/trade-store';
import { formatDisplay } from '@/utils/format-display';
import { formatNumberBalance } from '@/utils/numberFormat';
import BigNumber from 'bignumber.js';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from 'react-modal-sheet';
import { useShallow } from 'zustand/shallow';
// import { useGet24HVol } from '../hooks/useGet24hVol';
import CountdownUTC from './CountdownUTC';
type Props = {
    isOpen: boolean;
    onClose: () => void;
    pairIndex: string;
    pairInfo: Pair | undefined;
    price24h: number;
};

export function PriceChange({
    pairIndex,
    decimalsToShow,
}: {
    pairIndex: string;
    decimalsToShow: number;
}) {
    const data = usePrice(useShallow((state) => state.data));

    return (
        <div className=" top-0 flex items-end gap-2">
            <p
                className={clsx('text-[15px] text-white', {
                    '!text-[#FF695F]':
                        Number(data[pairIndex]?.price24h ?? 0) < 0,
                    '!text-[#38F24A]':
                        Number(data[pairIndex]?.price24h ?? 0) > 0,
                })}
            >
                {formatDisplay(Number(data[pairIndex]?.price24h ?? 0), {
                    decimalToShow: decimalsToShow,
                    minimumDecimalToShow: 2,
                })}
                %
            </p>
        </div>
    );
}

export default function SheetDetail({
    isOpen,
    pairIndex,
    pairInfo,
    onClose,
}: Props) {
    const { t } = useTranslation();
    const [first, last] = !pairInfo
        ? ['', '']
        : [pairInfo.symbol.split('USD')[0], 'USD'];

    const bookingInfo = useTradeStore((state) => state.booking);

    // const { formattedData } = useGet24HVol();

    const bookingInfoPair = bookingInfo.find(
        (item) => item.pair_index === Number(pairIndex)
    );

    // const pair24HVol = formattedData?.find(
    //     (item) => item.pairIndex === Number(pairIndex)
    // );

    const fundingRate = useMemo(() => {
        return `${formatDisplay(
            Math.abs(Number(bookingInfoPair?.funding_rate_long || 0) * 100),
            {
                decimalToShow: 5,
                minimumDecimalToShow: 5,
                disableSmallValue: true,
            }
        )}%`;
    }, [bookingInfoPair?.funding_rate_long]);

    return (
        <Sheet isOpen={isOpen} onClose={onClose} detent="content-height">
            <Sheet.Backdrop className="!bg-transparent3" onTap={onClose} />

            <Sheet.Container className="!bg-transparent p-4  mb-2 flex !flex-col justify-end">
                <Sheet.Content className="h-fit !bg-transparent flex !flex-col justify-end z-50">
                    <div className="flex flex-col  !bg-[#141414] mt-auto p-6 h-fit !rounded-4xl">
                        <div className="flex justify-between items-center">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                                fill="none"
                            >
                                <path
                                    d="M9.22266 19.8054L13.2133 14.62L17.7653 18.1934L21.6706 13.1533"
                                    stroke="white"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M26.2228 3.1333C27.6388 3.1333 28.7855 4.27997 28.7855 5.69597C28.7855 7.11063 27.6388 8.25863 26.2228 8.25863C24.8068 8.25863 23.6602 7.11063 23.6602 5.69597C23.6602 4.27997 24.8068 3.1333 26.2228 3.1333Z"
                                    stroke="white"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M27.6737 12.3588C27.851 13.5522 27.9323 14.8962 27.9323 16.4042C27.9323 25.6548 24.8497 28.7375 15.599 28.7375C6.34963 28.7375 3.26562 25.6548 3.26562 16.4042C3.26562 7.1548 6.34963 4.0708 15.599 4.0708C17.079 4.0708 18.4003 4.14947 19.5763 4.32013"
                                    stroke="white"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>

                            <button
                                onClick={() => {
                                    console.debug('ok');
                                    onClose();
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <g clip-path="url(#clip0_2724_7135)">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M11.9993 13.4141L17.6563 19.0711C17.8449 19.2533 18.0975 19.3541 18.3597 19.3518C18.6219 19.3495 18.8727 19.2444 19.0582 19.059C19.2436 18.8736 19.3487 18.6227 19.351 18.3606C19.3533 18.0984 19.2525 17.8458 19.0703 17.6571L13.4133 12.0001L19.0703 6.34315C19.2525 6.15455 19.3533 5.90194 19.351 5.63975C19.3487 5.37755 19.2436 5.12674 19.0582 4.94133C18.8727 4.75592 18.6219 4.65075 18.3597 4.64848C18.0975 4.6462 17.8449 4.74699 17.6563 4.92915L11.9993 10.5861L6.34233 4.92915C6.15288 4.75149 5.90175 4.65451 5.64207 4.65873C5.38238 4.66295 5.13453 4.76803 4.95094 4.95174C4.76736 5.13546 4.66245 5.38339 4.65842 5.64307C4.65439 5.90276 4.75155 6.15382 4.92933 6.34315L10.5853 12.0001L4.92833 17.6571C4.83282 17.7494 4.75664 17.8597 4.70423 17.9817C4.65182 18.1037 4.62424 18.235 4.62308 18.3677C4.62193 18.5005 4.64723 18.6322 4.69751 18.7551C4.74779 18.878 4.82205 18.9897 4.91594 19.0835C5.00983 19.1774 5.12148 19.2517 5.24438 19.302C5.36728 19.3523 5.49896 19.3776 5.63174 19.3764C5.76452 19.3752 5.89574 19.3477 6.01774 19.2953C6.13974 19.2428 6.25009 19.1667 6.34233 19.0711L11.9993 13.4141Z"
                                            fill="#4C4C4C"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_2724_7135">
                                            <rect
                                                width="24"
                                                height="24"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </button>
                        </div>

                        <p className="text-white mt-3 text-2xl mb-4">
                            {first}/{last}
                        </p>

                        <div className="flex justify-between items-center mb-4">
                            <p className="text-[15px] text-[#4C4C4C]">
                                {t('24H Change')}
                            </p>

                            <PriceChange
                                pairIndex={pairIndex}
                                decimalsToShow={pairInfo?.decimalsToShow ?? 5}
                            />
                        </div>

                        <div className="flex justify-between items-center mb-4 text-[15px]">
                            <p className=" text-[#4C4C4C]">
                                {t('Open Interest')}
                            </p>

                            <p className="text-white">
                                {formatNumberBalance(
                                    new BigNumber(
                                        Number(
                                            bookingInfoPair?.open_interest_long ??
                                                0
                                        )
                                    )
                                        .plus(
                                            new BigNumber(
                                                Number(
                                                    bookingInfoPair?.open_interest_short ??
                                                        0
                                                )
                                            )
                                        )
                                        .dividedBy(1e18)
                                        .toNumber()
                                )}
                            </p>
                        </div>

                        {/* <div className="flex justify-between items-center mb-4 text-[15px]">
                            <p className=" text-[#4C4C4C]">{t('24H Volume')}</p>

                            <p className="text-white">
                                {formatNumberBalance(pair24HVol?.volume ?? 0)}
                            </p>
                        </div> */}

                        <div className="flex justify-between items-center mb-4 text-[15px]">
                            <p className=" text-[#4C4C4C]">
                                {t('Est. 1H Funding')}
                            </p>

                            <p className="text-white">{fundingRate}</p>
                        </div>

                        <div className="flex justify-between items-center mb-4 text-[15px]">
                            <p className=" text-[#4C4C4C]">{t('Countdown')}</p>

                            <CountdownUTC />
                        </div>

                        <div className="flex justify-between items-center mb-4 text-[15px]">
                            <p className=" text-[#4C4C4C]">{t('Spread')}</p>

                            <p className="text-white">
                                {bookingInfoPair?.spread}%
                            </p>
                        </div>
                    </div>
                </Sheet.Content>
            </Sheet.Container>
        </Sheet>
    );
}
